/**
 * Zahlenleiste des Vorgangs — seit der Variante „Salon“ (Antoine, 26.09.2026, Entscheidung E4) eine
 * flache Leiste mit drei Feldern statt fünf Kacheln: Angebotswert · Zahlungen (Balken bezahlt/offen,
 * Nächste Fälligkeit) · Kostenübernahme. Leere Werte stehen grau statt so groß wie ein echter
 * Betrag. Die frühere zweite Zahlenzeile in der Abrechnungskarte entfällt; deren Hinweise (Plan
 * anpassen, Zahlungslinks, Rückfall) hängen als `children` unter der Leiste.
 *
 * Jedes Feld führt auf `/vorgaenge/:id/abrechnung` — die Abrechnungs-Unterseite bleibt die einzige
 * Wahrheit für Aktionen (Kostenübernahme anfordern, Rechnung stellen, Zahlung erfassen).
 *
 * **Ausnahme seit A3 (Antoine, 13.09.2026):** existiert eine Kostenübernahme, öffnet ihr Feld ein
 * Drawer mit der vollständigen Darstellung (`KostenuebernahmeDetail`, dieselbe Komponente wie auf
 * der Abrechnungsseite). Das Drawer zeigt, es handelt nicht; aus ihm führt ein Link zur Abrechnung.
 *
 * Rechenlogik lebt in `lib/zahlungs-kennzahlen.ts` (geteilt mit `pages/abrechnung.tsx`). Beträge
 * kommen nur aus bestehenden Feldern: `amountTotalCents` des Vorgangs und die Zahlungen.
 */
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Card, Drawer, Icon, StatusPill } from "../components";
import { formatDate, formatDateTime, money } from "../format";
import { kennzahlenFuerVorgang, type AbrechnungsDeckung, kostenuebernahmeStand } from "../../lib/zahlungs-kennzahlen";
import { GRUEN_FUELLUNG, GRUEN_PILL, GRUEN_RAHMEN, bezahltBeschriftung, bezahltDatenZeile, type ZahlungsPosten } from "../../lib/zahlungsstand";
import { abrechnungPfad } from "../../lib/pfade";
import { A_LA_CARTE_ABRECHNUNG_VOR_ORT } from "../../lib/a-la-carte";
import { KostenuebernahmeDetail, type CostAcceptance } from "./KostenuebernahmeDetail";

export interface AuftragKennzahlenProps {
  eventId: string;
  amountTotalCents: number | null;
  payments: ZahlungsPosten[];
  /** `null`/`undefined` = noch nie angefordert (`GET /api/events/:id/cost-acceptance` -> null). */
  costAcceptance: CostAcceptance | null | undefined;
  /** Kleine Zeile unter dem Angebotswert, z. B. „Menü · 12 Gäste · 83,75 € pro Gast“. */
  angebotZeile?: string | null;
  /** Migration 731: die Variante ist „À la carte“ ohne Betrag — statt „0,00 €“ steht „à la carte · Abrechnung vor Ort“. */
  aLaCarteOhneBetrag?: boolean;
  /** Abrechnung Stufe 3: mit abgeschlossener Abrechnung deren Soll/Bezahlt/Offen (Server, sql/574). */
  abrechnungDeckung?: AbrechnungsDeckung | null;
  /** Hinweise der Abrechnung (Plan anpassen, Zahlungslinks …) unter der Leiste. */
  children?: ReactNode;
}

const LABEL = "text-[10.5px] font-extrabold uppercase tracking-[0.09em] text-secondary-fixed-dim";
const FELD = "flex min-w-0 flex-col gap-1.5 px-5 py-4 transition-colors hover:bg-surface-container-low dark:hover:bg-white/[0.03] sm:px-6";
const KLEIN = "text-[11.5px] text-secondary-fixed-dim";
const LEER = "text-secondary-fixed-dim";
const VOLL = "text-on-background dark:text-inverse-on-surface";

export function AuftragKennzahlen({ eventId, amountTotalCents, payments, costAcceptance, angebotZeile, aLaCarteOhneBetrag = false, abrechnungDeckung, children }: AuftragKennzahlenProps) {
  // Abrechnung Stufe 3: nach dem Abschluss misst die Leiste am Abrechnungsbetrag (inkl. Zahlungen außerhalb).
  const kz = kennzahlenFuerVorgang(payments, { amountTotalCents, abrechnungDeckung });
  const ca = kostenuebernahmeStand(costAcceptance);
  const abrechnungHref = abrechnungPfad(eventId);
  const [kostenuebernahmeOffen, setKostenuebernahmeOffen] = useState(false);
  const summe = kz.sollCents ?? 0;
  const anteil = (cents: number) => (summe > 0 ? `${Math.min(100, Math.max(0, (cents / summe) * 100)).toFixed(1)}%` : "0%");

  const kuInhalt = (
    <>
      <span className={LABEL}>Kostenübernahme</span>
      <span>
        <StatusPill meta={ca} />
      </span>
      <span className={KLEIN}>
        {costAcceptance?.status === "signed" && costAcceptance.signedAt
          ? `Signiert am ${formatDateTime(costAcceptance.signedAt)}`
          : costAcceptance
            ? "Details ansehen"
            : "In der Abrechnung anfordern"}
      </span>
    </>
  );

  return (
    <Card id="abrechnung" className="scroll-mt-20 overflow-hidden !p-0" data-testid="auftrag-kennzahlen">
      <div className="grid grid-cols-1 divide-y divide-surface-container dark:divide-white/[0.06] md:grid-cols-[1.1fr_1.6fr_1fr] md:divide-x md:divide-y-0">
        <Link to={abrechnungHref} className={FELD}>
          <span className={LABEL}>{kz.ausAbrechnung ? "Abrechnung" : aLaCarteOhneBetrag ? "Angebot" : "Angebotswert"}</span>
          {aLaCarteOhneBetrag && !kz.ausAbrechnung ? (
            <span data-testid="angebotswert-a-la-carte" className={`text-[19px] font-extrabold leading-tight tracking-[-0.02em] sm:text-[21px] ${VOLL}`}>
              {A_LA_CARTE_ABRECHNUNG_VOR_ORT}
            </span>
          ) : (
            <span className={`text-[26px] font-extrabold leading-tight tracking-[-0.025em] tabular-nums sm:text-[30px] ${kz.sollCents == null ? LEER : VOLL}`}>
              {money(kz.sollCents)}
            </span>
          )}
          {kz.ausAbrechnung ? <span className={KLEIN}>abgeschlossen · Angebot {money(amountTotalCents)}</span> : angebotZeile && <span className={KLEIN}>{angebotZeile}</span>}
        </Link>

        <Link
          to={abrechnungHref}
          className={`${FELD} ${kz.vollstaendigBezahlt ? `m-1 rounded-xl border ${GRUEN_RAHMEN}` : ""}`}
          data-testid={kz.vollstaendigBezahlt ? "zahlungen-gedeckt" : undefined}
        >
          <span className="flex items-center justify-between gap-2">
            <span className={LABEL}>Zahlungen</span>
            {kz.vollstaendigBezahlt && (
              <span
                data-testid="zahlungen-vollstaendig-pill"
                className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${GRUEN_PILL}`}
              >
                <Icon name="check_circle" className="text-[13px]" /> Vollständig bezahlt
              </span>
            )}
          </span>
          <span
            className="mt-1 flex h-2.5 overflow-hidden rounded-full bg-surface-container-high dark:bg-white/[0.08]"
            role="img"
            aria-label={`${money(kz.paidCents)} bezahlt, ${money(kz.openCents)} offen`}
          >
            <i className={`block h-full ${kz.vollstaendigBezahlt ? GRUEN_FUELLUNG : "bg-[#6F675A]"}`} style={{ width: kz.vollstaendigBezahlt ? "100%" : anteil(kz.paidCents) }} />
            <i className="block h-full bg-[#F0EAE0] dark:bg-white/[0.08]" style={{ width: kz.vollstaendigBezahlt ? "0%" : anteil(kz.openCents) }} />
          </span>
          <span className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 text-[12.5px]">
            <span className={kz.paidCents > 0 ? VOLL : LEER}>
              <b className="font-extrabold tabular-nums">{money(kz.paidCents)}</b>{" "}
              {kz.paidCents > 0 ? bezahltBeschriftung(payments, kz.vollstaendigBezahlt) : "Bezahlt"}
            </span>
            <span className={kz.openCents > 0 ? VOLL : LEER}>
              <b className="font-extrabold tabular-nums">{money(kz.openCents)}</b> offen
            </span>
          </span>
          <span className={KLEIN}>
            {kz.vollstaendigBezahlt ? (
              (bezahltDatenZeile(payments) ?? "Alle Zahlungen sind eingegangen.")
            ) : payments.length === 0 ? (
              "Noch keine Zahlungen erfasst."
            ) : (
              <>
                Nächste Fälligkeit: <span className="font-semibold">{kz.naechsteFaelligkeit ? formatDate(kz.naechsteFaelligkeit) : "—"}</span>
              </>
            )}
          </span>
        </Link>

        {/* A3: mit Kostenübernahme -> Drawer, ohne -> Link auf die Abrechnungs-Unterseite. */}
        {costAcceptance ? (
          <button type="button" className={`${FELD} text-left`} onClick={() => setKostenuebernahmeOffen(true)}>
            {kuInhalt}
          </button>
        ) : (
          <Link to={abrechnungHref} className={FELD}>
            {kuInhalt}
          </Link>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-surface-container px-5 py-2.5 dark:border-white/[0.06] sm:px-6">
        <Link to={abrechnungHref} className="inline-flex items-center gap-1 font-label-md text-[12px] font-bold text-primary hover:underline">
          <Icon name="receipt_long" className="text-[16px]" /> Kostenübernahme, Rechnungen &amp; Zahlungen
        </Link>
      </div>
      {children && <div className="space-y-3 border-t border-surface-container px-5 py-4 empty:hidden dark:border-white/[0.06] sm:px-6">{children}</div>}

      <Drawer
        open={kostenuebernahmeOffen}
        onClose={() => setKostenuebernahmeOffen(false)}
        title="Kostenübernahme"
        subtitle="Alles, was vom eSign-Anbieter zurückgekommen ist — Aktionen liegen weiterhin in der Abrechnung."
      >
        <div className="min-w-0" data-testid="kostenuebernahme-drawer">
          <KostenuebernahmeDetail eventId={eventId} costAcceptance={costAcceptance} />
        </div>
        <Link
          to={abrechnungHref}
          className="mt-5 inline-flex items-center gap-1.5 font-label-md text-label-md text-primary hover:underline"
        >
          <Icon name="receipt_long" className="text-[16px]" /> Zur Abrechnung
        </Link>
      </Drawer>
    </Card>
  );
}
